import { useAnalytics } from "@codat/analytics";
import { Avatar, ClipboardCopy, Typography, useToast } from "@codat/orchard-ui";
import { ClientType, UserState } from "api/public";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import React, { useCallback } from "react";
import { truncateMiddle } from "utils/truncateMiddle";

import ClientTypeLabel from "components/ClientTypeLabel";
import UpgradeButton from "components/UpgradeButton";

import { getInstanceType } from "./UserClientsDropdown";
import styles from "./UserClientsDropdown.module.scss";

interface CurrentClientSummaryProps {
  clientName: string;
  clientType: ClientType;
  clientId: string;
  iconSrc: string | undefined;
  showUpgradeButton: boolean;
  userState: UserState;
}

const CurrentClientSummary = ({
  clientName,
  clientType,
  iconSrc,
  clientId,
  showUpgradeButton,
  userState,
}: CurrentClientSummaryProps) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();
  const { featureStatus } = useFeatureStatus();

  const hideStartUpPlan = featureStatus?.["PortalUI.HideStartUpPlan"] ?? false;

  const onCopyClientId = useCallback(() => {
    addToast({
      variant: "success",
      message: "Copied client ID",
    });
    sendEvent("Copy Client ID Button - Click");
  }, [sendEvent, addToast]);

  const onCopyClientIdError = useCallback(() => {
    addToast({
      variant: "error",
      message: "Failed to copy client ID",
    });
    sendEvent("Copy Client ID Button - Click");
  }, [sendEvent, addToast]);

  return (
    <div>
      <div className={styles.summaryHeader}>
        <Avatar alt={clientName} src={iconSrc} color="interface" />
        <div>
          <Typography
            variant="heading_sm"
            as="div"
            className={styles.clientName}
          >
            {clientName}
          </Typography>
          <Typography
            className={styles.clientType}
            data-testid="nav-client-type"
            variant="body_md"
            as="div"
          >
            {getInstanceType(clientType)}
          </Typography>
        </div>
      </div>
      <dl className={styles.details}>
        <Typography variant="body_sm" as="dt">
          Client type:
        </Typography>
        <Typography variant="body_sm" as="dd">
          <ClientTypeLabel clientType={clientType} />
        </Typography>
        <Typography variant="body_sm" as="dt">
          Client ID:
        </Typography>
        <Typography variant="body_sm" as="dd" className={styles.clientId}>
          {truncateMiddle(clientId)}
          <ClipboardCopy
            className={styles.copyButton}
            label="Copy client ID to clipboard"
            data-testid="copy-clientid-button"
            iconClassName={styles.copyButtonIcon}
            onCopy={onCopyClientId}
            onError={onCopyClientIdError}
            noBackground
            text={clientId}
            color="interface"
          />
        </Typography>
      </dl>
      {showUpgradeButton && (
        <div className={styles.summaryFooter}>
          <UpgradeButton
            userState={userState}
            hideStartUpPlan={hideStartUpPlan}
            className={styles.upgradeBtn}
          />
        </div>
      )}
    </div>
  );
};

export default CurrentClientSummary;
